import { Component, inject, Input, OnInit } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { MembershipService, ApplicantProduct } from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { firstValueFrom } from 'rxjs';

/** OAO checking and saving accounts component */
@Component({
  selector: 'ecp-oao-other-account-member-service',
  templateUrl: './oao-other-member-services.component.html'
})
export class OaoOtherAccountMembershipServiceComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  /** oao Service objcet */
  @Input() public mainService: MembershipService[];

  /** all membership services */
  @Input() public allServices: MembershipService[];

  /** co Applicant Services */
  @Input() public coApplicantServices: ApplicantProduct[];

  /** Is Initial Loading from L360 Call */
  @Input() public isInitialLoading: boolean;

  /** Co Applicants Services : Local Variable */
  public coAppServices: ApplicantProduct[];

  /** Storage ID */
  public arrayStorageId = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/membershipServices';
  public coAppStorageId = 'api:oaoMemberInformation/accountsAndServices/coApplicant/membershipServices/$/isSelected';
  public additionalCoAppStorageId = 'api:oaoMemberInformation/accountsAndServices/additionalCoApplicant/membershipServices/$/isSelected';
  public responsibleParties4StorageId = 'api:oaoMemberInformation/accountsAndServices/responsibleParties4/membershipServices/$/isSelected';

  /** showing checking account flag */
  public showAccounts = false;

  constructor(public readonly applicationApiService: ApplicationApiService, public readonly l360Service: L360Service) {
    this.coAppServices = [];
  }

  public ngOnInit(): void {
    this.coAppServices = this.coApplicantServices.map((x) => Object.assign({}, x));
    // Set accordian Open status if any required field is active
    const isAnyServiceOptOut = this.mainService.filter((f) => f.requiredOptInType.toLowerCase() === 'opt-out').length > 0 ? true : false;
    if (isAnyServiceOptOut && this.isInitialLoading) {
      this.showAccounts = true;
    }
  }

  /** Account display toggle */
  public AccountsToggler(): void {
    this.showAccounts = !this.showAccounts;
  }

  /** get acocunt title */
  public get title(): string {
    return this.mainService.length > 0 ? this.mainService[0].relatedProductTypeName : '';
  }

  /** get account description */
  public get description(): string {
    return this.mainService.length > 0 ? this.mainService[0].description : '';
  }

  /** Saves the user selection to session storage */
  public updateSelection(service: MembershipService): void {
    this.statefulService.wrap(this.arrayStorageId + '/$/isSelected', service.bundleIndex).value = service.isSelected;

    this.coAppServices.forEach((applicant) => {
      applicant.membershipServices
        .filter((f) => f.relatedProductId === service.relatedProductId)
        .forEach((share) => {
          // if Primary Applicant's service unSelected, then clear all Co-Applicant's selection
          if (!service.isSelected) {
            share.isSelected = false;
            if (this.hasCoApplicant()) {
              this.setCoApplicantSelection(service.bundleIndex, false);
            }
          } else {
            // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
            if (this.isYouthMembership() && service.availableForJointOwner) {
              share.isSelected = true;
              this.setCoApplicantSelection(service.bundleIndex, true);
            }
          }
        });
    });

    this.statefulService.wrap('ss:oao-accounts-membership-services').value = JSON.stringify(this.allServices);
  }

  public setCoApplicantSelection(bunldeIndex: number, val: boolean) {
    // bundle update for co-applicants
    if (this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/coApplicant/applicantId').value) {
      this.statefulService.wrap(this.coAppStorageId, bunldeIndex).value = val;
    }

    if (this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/additionalCoApplicant/applicantId').value) {
      this.statefulService.wrap(this.additionalCoAppStorageId, bunldeIndex).value = val;
    }

    if (this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/responsibleParties4/applicantId').value) {
      this.statefulService.wrap(this.responsibleParties4StorageId, bunldeIndex).value = val;
    }
  }

  /** getCurrentArraySize */
  public getCurrentArraySize(): number {
    return this.statefulService.arraySize(this.arrayStorageId);
  }

  // On Selection of Co Applicant's Share checkbox
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public coAppMembershipServiceSelection(bundlePath: string, index: number, val: any): void {
    const rawReqBody = [
      {
        op: 'add',
        path: `oaoMemberInformation/accountsAndServices/${bundlePath}/membershipServices/${index}/isSelected`,
        value: val
      }
    ];
    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.statefulService.wrap('ss:oao-accounts-membership-services').value = JSON.stringify(this.allServices);
  }

  /** Check has coapplicants */
  public hasCoApplicant(): boolean {
    const membershipType = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value;
    if (membershipType && membershipType.toString().toLowerCase() === 'business') {
      const responsibleParties = this.statefulService.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      return responsibleParties && responsibleParties > 1;
    }
    return this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
  }

  /** Check has optional Services */
  public hasCoApplicantServices(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantServices = [];
    this.coAppServices.forEach((applicant) => {
      if (applicant.membershipServices[indexParent]) {
        hasCoApplicantServices.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantServices.length > 0 && hasCoApplicantServices.filter((f) => f == true).length > 0;
  }

  public isYouthMembership(): boolean {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  }

  /**
   * Simple function for automated test id generation to determine if a product is a credit card or debit card
   * @param productName - string
   * @returns string -  'credit' | 'debit' | 'other'
   */
  public isCreditOrDebit(productName: string) {
    let type = 'other';
    // Check if productName contains 'Credit' or 'Debit Card'

    // Convert the productName to lowercase for case-insensitive comparison
    const lowerProductName = productName.toLowerCase();

    // Check if productName contains 'credit' or 'debit'
    if (lowerProductName.includes('credit')) {
      type = 'credit';
    } else if (lowerProductName.includes('debit')) {
      type = 'debit';
    }

    return type;
  }
}
